(() => {
  'use strict';
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const wrap = (i,n) => ((i % n) + n) % n;

  class ArtHero extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.slides = [...this.querySelectorAll('[data-aw-slide]')];
      this.index = 0;
      if (!this.slides.length) return;
      this.events = new AbortController();
      const opts = { signal: this.events.signal };
      this.querySelector('[data-aw-prev]')?.addEventListener('click', () => this.go(-1), opts);
      this.querySelector('[data-aw-next]')?.addEventListener('click', () => this.go(1), opts);
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.go(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, opts);
      this.addEventListener('pointerdown', event => {
        if (event.button !== 0 || event.target.closest('button')) return;
        this.pointer = {x:event.clientX,y:event.clientY,id:event.pointerId,drag:false};
      }, opts);
      this.addEventListener('pointermove', event => {
        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.x;
        if (!this.pointer.drag && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(event.clientY - this.pointer.y)) {
          this.pointer.drag = true;
          this.classList.add('is-dragging'); this.setPointerCapture(event.pointerId);
        }
        if (this.pointer.drag) this.draw(dx);
      }, opts);
      const finish = event => {
        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.x;
        this.suppressClick = this.pointer.drag;
        this.pointer = null; this.classList.remove('is-dragging');
        if (event.type !== 'pointercancel' && Math.abs(dx) > 45 && this.suppressClick) this.go(dx > 0 ? -1 : 1);
        else this.draw();
      };
      this.addEventListener('pointerup', finish, opts);
      this.addEventListener('pointercancel', finish, opts);
      this.addEventListener('click', event => { if (this.suppressClick) { event.preventDefault(); this.suppressClick = false; } }, {...opts,capture:true});
      this.addEventListener('dragstart', event => event.preventDefault(), opts);
      this.addEventListener('wheel', event => {
        if (Math.abs(event.deltaX) < Math.abs(event.deltaY) || Math.abs(event.deltaX) < 18) return;
        event.preventDefault();
        if (Date.now() - (this.lastWheel || 0) > 550) { this.go(event.deltaX > 0 ? 1 : -1); this.lastWheel = Date.now(); }
      }, {...opts,passive:false});
      this.draw();
    }
    go(direction) {
      this.index = wrap(this.index + direction,this.slides.length);
      this.draw();
      this.querySelector('[data-aw-status]').textContent = this.slides[this.index].dataset.awTitle;
    }
    draw(drag=0) {
      const step = innerWidth < 750 ? innerWidth * .715 : innerWidth * .5;
      this.slides.forEach((slide,index) => {
        let offset = wrap(index - this.index,this.slides.length);
        if (offset > this.slides.length / 2) offset -= this.slides.length;
        const active = index === this.index;
        const previous = Number(slide.dataset.offset);
        if (Math.abs(previous - offset) > 1 && !motion.matches) {
          slide.style.transition = 'none';
          requestAnimationFrame(() => { slide.style.transition = ''; });
        }
        slide.dataset.offset = offset;
        slide.style.setProperty('--aw-offset', offset + drag / step);
        slide.style.setProperty('--aw-scale', active ? 1 : .8);
        slide.classList.toggle('is-active',active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a').forEach(link => link.tabIndex = active ? 0 : -1);
      });
    }
    disconnectedCallback() { this.events?.abort(); this.ready = false; }
  }

  class ArtRail extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      this.track = this.querySelector('[data-aw-track]');
      if (!this.track) return;
      this.events = new AbortController(); const opts = {signal:this.events.signal};
      this.prev = this.querySelector('[data-aw-prev]'); this.next = this.querySelector('[data-aw-next]');
      this.prev?.addEventListener('click', () => this.move(-1),opts);
      this.next?.addEventListener('click', () => this.move(1),opts);
      this.track.addEventListener('scroll', () => this.edges(), {...opts,passive:true});
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (['ArrowLeft','ArrowRight'].includes(event.key)) { event.preventDefault(); this.move(event.key === 'ArrowLeft' ? -1 : 1); }
      },opts);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,[data-af-card-gallery]')) return;
        this.pointer = {x:event.clientX,scroll:this.track.scrollLeft,id:event.pointerId}; this.dragged = false;
      },opts);
      this.track.addEventListener('pointermove', event => {
        if (!this.pointer) return;
        const dx = event.clientX - this.pointer.x;
        if (Math.abs(dx) > 8) {
          this.dragged = true; this.track.setPointerCapture(event.pointerId);
          this.track.style.scrollSnapType = 'none'; this.track.scrollLeft = this.pointer.scroll - dx;
        }
      },opts);
      const finish = () => { this.pointer = null; this.track.style.scrollSnapType = ''; };
      this.track.addEventListener('pointerup',finish,opts); this.track.addEventListener('pointercancel',finish,opts);
      this.track.addEventListener('click', event => { if (this.dragged) { event.preventDefault(); this.dragged = false; } },{...opts,capture:true});
      this.track.addEventListener('dragstart',event=>event.preventDefault(),opts);
      this.resize = new ResizeObserver(() => this.edges()); this.resize.observe(this.track); this.edges();
    }
    move(direction) { const width = this.track.firstElementChild?.getBoundingClientRect().width || this.track.clientWidth; this.track.scrollBy({left:direction * (width + 16),behavior:motion.matches ? 'instant' : 'smooth'}); }
    edges() { if(this.prev)this.prev.disabled=this.track.scrollLeft<3; if(this.next)this.next.disabled=this.track.scrollLeft+this.track.clientWidth>=this.track.scrollWidth-3; }
    disconnectedCallback() { this.events?.abort(); this.resize?.disconnect(); this.ready=false; }
  }

  class ArtStories extends HTMLElement {
    connectedCallback() {
      if(this.ready)return; this.ready=true;
      this.panels=[...this.querySelectorAll('[data-aw-panel]')]; this.tabs=[...this.querySelectorAll('[data-aw-tab]')]; this.pause=this.querySelector('[data-aw-pause]'); this.index=0; this.paused=false; this.visible=false;
      this.events=new AbortController(); const opts={signal:this.events.signal};
      this.tabs.forEach((tab,index)=>{
        tab.addEventListener('click',()=>{this.select(index);},opts);
        tab.addEventListener('keydown',event=>{
          if(!['ArrowLeft','ArrowRight','Home','End'].includes(event.key))return;
          event.preventDefault(); const next=event.key==='Home'?0:event.key==='End'?this.tabs.length-1:wrap(index+(event.key==='ArrowRight'?1:-1),this.tabs.length);
          this.select(next);this.tabs[next].focus();
        },opts);
      });
      this.querySelector('[data-aw-prev]')?.addEventListener('click',()=>this.select(wrap(this.index-1,this.panels.length)),opts);
      this.querySelector('[data-aw-next]')?.addEventListener('click',()=>this.select(wrap(this.index+1,this.panels.length)),opts);
      this.pause?.addEventListener('click',()=>{this.paused=!this.paused;this.schedule();},opts);
      this.addEventListener('mouseenter',()=>{this.hover=true;this.schedule();},opts);this.addEventListener('mouseleave',()=>{this.hover=false;this.schedule();},opts);
      this.addEventListener('focusin',()=>{this.focused=true;this.schedule();},opts);this.addEventListener('focusout',event=>{if(!this.contains(event.relatedTarget)){this.focused=false;this.schedule();}},opts);
      document.addEventListener('visibilitychange',()=>this.schedule(),opts);
      motion.addEventListener('change',()=>this.schedule(),opts);
      this.observer=new IntersectionObserver(entries=>{this.visible=entries[0].isIntersecting;this.schedule();},{threshold:.25});this.observer.observe(this);this.select(0);
    }
    select(index) {
      if(!this.panels.length)return;this.index=index;
      this.panels.forEach((panel,i)=>{panel.hidden=i!==index;});
      this.tabs.forEach((tab,i)=>{const selected=i===index;if(tab.getAttribute('role')==='tab'){tab.setAttribute('aria-selected',String(selected));tab.tabIndex=selected?0:-1;}else tab.setAttribute('aria-pressed',String(selected));});
      if(this.tabs[index]?.getAttribute('role')==='tab'){const tab=this.tabs[index];const rail=tab.parentElement;rail.scrollTo({left:tab.offsetLeft-rail.offsetLeft,behavior:motion.matches?'instant':'smooth'});}
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);const reduced=motion.matches;
      this.classList.toggle('is-paused',this.paused||reduced||!this.visible||this.hover||this.focused||document.hidden);
      if(this.pause){this.pause.disabled=reduced;this.pause.textContent=reduced?'Motion paused':this.paused?'Play':'Pause';this.pause.setAttribute('aria-label',reduced?'Automatic motion disabled':this.paused?'Play automatic rotation':'Pause automatic rotation');}
      if(this.paused||reduced||!this.visible||this.hover||this.focused||document.hidden||this.panels.length<2)return;
      this.timer=setTimeout(()=>this.select(wrap(this.index+1,this.panels.length)),Number(this.dataset.awInterval)||5500);
    }
    disconnectedCallback(){clearTimeout(this.timer);this.events?.abort();this.observer?.disconnect();this.ready=false;}
  }

  class ArtShowcase extends HTMLElement {
    connectedCallback(){
      if(this.ready)return;this.ready=true;this.images=[...this.querySelectorAll('[data-aw-floating]')];this.events=new AbortController();
      const queue=()=>{if(!this.frame)this.frame=requestAnimationFrame(()=>{this.frame=0;this.update();});};
      window.addEventListener('scroll',queue,{passive:true,signal:this.events.signal});window.addEventListener('resize',queue,{signal:this.events.signal});motion.addEventListener('change',queue,{signal:this.events.signal});this.update();
    }
    update(){
      if(motion.matches){this.images.forEach(image=>{image.style.opacity='.6';image.style.transform='none';});return;}
      const rect=this.getBoundingClientRect();const progress=Math.min(1,Math.max(0,-rect.top/Math.max(1,rect.height-innerHeight)));
      this.images.forEach((image,index)=>{const local=progress*this.images.length-index;const show=Math.max(0,Math.min(1,local*3,(1.75-local)*2));image.style.opacity=String(show*.8);image.style.transform=`translateY(${(1-show)*65}px) scale(${.7+show*.3})`;});
    }
    disconnectedCallback(){this.events?.abort();cancelAnimationFrame(this.frame);this.ready=false;}
  }
  [['af-wrong-hero',ArtHero],['af-wrong-rail',ArtRail],['af-wrong-stories',ArtStories],['af-wrong-showcase',ArtShowcase]].forEach(([name,component])=>{if(!customElements.get(name))customElements.define(name,component);});
})();
